import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import React from 'react';
import appReleasesService from '../services/app-releases-service';

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

/**
 * Per-platform release history with row actions. `notify(msg, severity)` shows
 * a snackbar; `onChanged()` refetches after an action.
 */
function ReleaseList({ releases, loading, platform, onChanged, notify }) {
  const say = (message, severity = 'success') =>
    notify && notify(message, severity);

  const copyLink = (release) => {
    const url =
      platform === 'ios' ? release.installUrl : release.downloadUrl;
    if (!url) {
      say('No link available for this build.', 'error');
      return;
    }
    navigator.clipboard
      .writeText(url)
      .then(() =>
        say(platform === 'ios' ? 'Install link copied!' : 'Download link copied!')
      )
      .catch(() => say('Failed to copy.', 'error'));
  };

  const run = async (fn, okMsg) => {
    try {
      const res = await fn();
      if (res && res.success) {
        say(okMsg);
        if (onChanged) onChanged();
      } else {
        say((res && res.reason) || 'Action failed.', 'error');
      }
    } catch (err) {
      say(err.message || 'Action failed.', 'error');
    }
  };

  const handleSetLatest = (r) =>
    run(() => appReleasesService.setLatest(r.id), 'Marked as latest.');

  const handleToggleMandatory = (r) =>
    run(
      () => appReleasesService.setMandatory(r.id, !r.isMandatory),
      r.isMandatory ? 'Mandatory turned off.' : 'Mandatory turned on.'
    );

  const handleDelete = (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${platform} build ${r.versionName} (code ${r.versionCode})?`)) {
      return;
    }
    run(() => appReleasesService.deleteRelease(r.id), 'Release deleted.');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!releases || releases.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body1" color="text.secondary">
          No {platform} releases uploaded yet.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell>
              <strong>Version</strong>
            </TableCell>
            <TableCell>
              <strong>Code</strong>
            </TableCell>
            <TableCell>
              <strong>Size</strong>
            </TableCell>
            <TableCell>
              <strong>Uploaded</strong>
            </TableCell>
            <TableCell>
              <strong>Status</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Mandatory</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Actions</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {releases.map((r) => (
            <TableRow
              key={r.id}
              sx={{ bgcolor: r.isLatest ? 'primary.50' : 'inherit' }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={r.isLatest ? 700 : 400}>
                  {r.versionName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" fontFamily="monospace">
                  {r.versionCode}
                </Typography>
              </TableCell>
              <TableCell>{formatFileSize(r.fileSize)}</TableCell>
              <TableCell>{formatDate(r.createdAt)}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  {r.isLatest ? (
                    <Chip label="Latest" color="success" size="small" />
                  ) : (
                    <Chip label="Older" size="small" variant="outlined" />
                  )}
                  {!r.hasArtifact && (
                    <Chip
                      label="No artifact"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="center">
                <Switch
                  size="small"
                  checked={!!r.isMandatory}
                  onChange={() => handleToggleMandatory(r)}
                />
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Set as latest">
                  <span>
                    <IconButton
                      size="small"
                      disabled={r.isLatest || !r.hasArtifact}
                      onClick={() => handleSetLatest(r)}
                    >
                      <StarIcon
                        fontSize="small"
                        color={r.isLatest ? 'warning' : 'inherit'}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    platform === 'ios'
                      ? 'Copy iOS install link'
                      : 'Copy APK download link'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!r.hasArtifact}
                      onClick={() => copyLink(r)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(r)}>
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ReleaseList;
